import { lazy } from 'react';

const FLAG = 'aan:chunk-reload-at';
const COOLDOWN_MS = 60_000;

const isChunkError = (error) => {
  const message = `${error?.message || ''} ${error?.name || ''}`;
  return /Loading chunk [\d]+ failed|ChunkLoadError|Loading CSS chunk|Importing a module script failed|dynamically imported module/i
    .test(message);
};

/**
 * `lazy`, but survives a deploy.
 *
 * A release replaces the hashed chunk files under any page that is already open, so the next
 * navigation asks for a filename that no longer exists and the import rejects. The user sees
 * "Loading chunk 6136 failed" through no fault of their own. The fix is to fetch the new
 * index.html once — which is all a reload does.
 *
 * Guarded by a timestamp so a genuinely broken chunk cannot put the tab in a reload loop.
 */
export const lazyWithRetry = (factory) => lazy(async () => {
  try {
    return await factory();
  } catch (error) {
    if (!isChunkError(error)) throw error;
    const last = Number(window.sessionStorage?.getItem(FLAG) || 0);
    if (Date.now() - last < COOLDOWN_MS) throw error;   // already tried — let the boundary show
    try {
      window.sessionStorage?.setItem(FLAG, String(Date.now()));
    } catch { /* private mode — reload anyway, the boundary catches a second failure */ }
    window.location.reload();
    return new Promise(() => {});                        // hold render; the page is on its way out
  }
});

export { isChunkError };
export default lazyWithRetry;
